import React, { useState, useEffect, useCallback } from "react";
import "./AdminPanel.css";
import API_URL from "./config";
import { Link } from "react-router-dom";
import expandArrow from "../assets/img/expand arrow.png";
import Loader from "./Loader";

function AdminPanel() {
  const [activeTab, setActiveTab] = useState("users");
  const [users, setUsers] = useState([]);
  const [activeUsers, setActiveUsers] = useState([]);
  const [inactiveUsers, setInactiveUsers] = useState([]);
  const [userListMode, setUserListMode] = useState("active"); // active или inactive

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Добавляем класс admin-page при монтировании и удаляем при размонтировании
  useEffect(() => {
    document.body.classList.add('admin-page');
    
    return () => {
      document.body.classList.remove('admin-page');
    };
  }, []);

  // Состояния для модального окна редактирования
  const [showEditModal, setShowEditModal] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [newRole, setNewRole] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [editError, setEditError] = useState(null);
  const [editSuccess, setEditSuccess] = useState(null);
  
  // Состояния для модального окна подтверждения активации/деактивации
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null);
  const [confirmSuccess, setConfirmSuccess] = useState(null);
  const [confirmError, setConfirmError] = useState(null);

  // Состояния для загрузки файлов
  const [weatherFile, setWeatherFile] = useState(null);
  const [sensorFiles, setSensorFiles] = useState([]);
  const [uploadResult, setUploadResult] = useState(null);
  const [uploadError, setUploadError] = useState(null);
  const [isUploading, setIsUploading] = useState(false);

  // Функция для определения активности пользователя
  const isUserActive = (user) => {
    // Проверяем, что поле is_active существует и имеет значение true
    // Важно использовать строгое сравнение ===
    return user.is_active === true;
  };

  // Загрузка всех пользователей
  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/get_all_user`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          "accept": "application/json"
        },
        credentials: 'include'
      });

      if (!response.ok) {
        throw new Error("Ошибка при загрузке пользователей");
      }

      const data = await response.json();
      setUsers(data);
    } catch (err) {
      setError("Не удалось загрузить пользователей: " + err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  // Загрузка активных пользователей
  const fetchActiveUsers = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/get_active_users`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          "accept": "application/json"
        },
        credentials: 'include'
      });

      if (response.status === 404) {
        // Если активные пользователи не найдены, устанавливаем пустой массив
        setActiveUsers([]);
        return;
      }

      if (!response.ok) {
        throw new Error("Ошибка при загрузке активных пользователей");
      }

      const data = await response.json();
      
      // Дополнительная проверка на фронтенде
      const validActiveUsers = data.filter(user => isUserActive(user));
      
      setActiveUsers(validActiveUsers);
    } catch (err) {
      setError("Не удалось загрузить активных пользователей: " + err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  // Загрузка неактивных пользователей
  const fetchInactiveUsers = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/get_inactive_users`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          "accept": "application/json"
        },
        credentials: 'include'
      });

      if (response.status === 404) {
        // Если неактивные пользователи не найдены, устанавливаем пустой массив
        setInactiveUsers([]);
        return;
      }

      if (!response.ok) {
        throw new Error("Ошибка при загрузке неактивных пользователей");
      }

      const data = await response.json();
      
      // Дополнительная проверка на фронтенде
      const validInactiveUsers = data.filter(user => !isUserActive(user));
      
      setInactiveUsers(validInactiveUsers);
    } catch (err) {
      setError("Не удалось загрузить неактивных пользователей: " + err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  // Загрузка ролей
  const fetchRoles = useCallback(async () => {
    setLoading(true);
    try {
      // Временное решение: пока используем тестовые данные
      // в будущем заменить на реальный эндпоинт API
      // Роли больше не используются в компоненте
    } catch (err) {
      setError("Не удалось загрузить роли: " + err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  // Загрузка данных при открытии страницы
  useEffect(() => {
    if (activeTab === "users") {
      if (userListMode === "active") {
        fetchActiveUsers();
      } else if (userListMode === "inactive") {
        fetchInactiveUsers();
      } else {
        fetchUsers();
      }
    } else if (activeTab === "roles") {
      fetchRoles();
    }
  }, [activeTab, userListMode, fetchActiveUsers, fetchInactiveUsers, fetchUsers, fetchRoles]);

  // Функция обновления роли пользователя
  const updateUserRole = async (email, newRoleId) => {
    try {
      const response = await fetch(`${API_URL}/update_role?email=${encodeURIComponent(email)}&new_role_id=${newRoleId}`, {
        method: "PUT",
        headers: {
          "accept": "application/json"
        },
        credentials: 'include'
      });

      if (!response.ok) {
        throw new Error("Ошибка при обновлении роли");
      }

      // Обновляем список пользователей
      if (userListMode === "active") {
        fetchActiveUsers();
      } else if (userListMode === "inactive") {
        fetchInactiveUsers();
      } else {
        fetchUsers();
      }
      
      return true;
    } catch (err) {
      throw err;
    }
  };

  // Функция обновления статуса активности пользователя
  const updateUserActivity = async (userId, isActive) => {
    try {
      setLoading(true); // Показываем индикатор загрузки
      
      const response = await fetch(`${API_URL}/update_user_activity?user_id=${userId}&is_active=${isActive}`, {
        method: "PUT",
        headers: {
          "accept": "application/json"
        },
        credentials: 'include'
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || "Ошибка при обновлении статуса пользователя");
      }

      // Показываем сообщение об успехе
      setError(null);
      
      // Обновляем списки пользователей
      await Promise.all([
        fetchActiveUsers(),
        fetchInactiveUsers()
      ]);
      
      // Обновляем текущий отображаемый список
      if (userListMode === "all") {
        fetchUsers();
      }
      
      return true;
    } catch (err) {
      setError(`Ошибка: ${err.message}`);
      return false;
    } finally {
      setLoading(false); // Скрываем индикатор загрузки
    }
  };

  // Функция обновления пароля пользователя
  const updateUserPassword = async (email, newPassword) => {
    try {
      const response = await fetch(`${API_URL}/update_password?email=${encodeURIComponent(email)}&new_password=${newPassword}`, {
        method: "PUT",
        headers: {
          "accept": "application/json"
        },
        credentials: 'include'
      });

      if (!response.ok) {
        throw new Error("Ошибка при обновлении пароля");
      }

      return true;
    } catch (err) {
      throw err;
    }
  };

  // Открытие модального окна редактирования
  const openEditModal = (user) => {
    setEditUser(user);
    setNewRole(user.role_id.toString());
    setNewPassword("");
    setEditError(null);
    setEditSuccess(null);
    setShowEditModal(true);
  };

  // Закрытие модального окна редактирования
  const closeEditModal = () => {
    setShowEditModal(false);
    setEditUser(null);
  };

  // Сохранение изменений пользователя
  const saveUserChanges = async () => {
    setEditError(null);
    setEditSuccess(null);
    
    try {
      // Если роль изменилась
      if (newRole && newRole !== editUser.role_id.toString()) {
        await updateUserRole(editUser.email, parseInt(newRole));
      }
      
      // Если пароль был введен
      if (newPassword) {
        await updateUserPassword(editUser.email, newPassword);
      }
      
      setEditSuccess("Изменения успешно сохранены");
      
      // Закрываем модальное окно после небольшой задержки
      setTimeout(() => {
        closeEditModal();
      }, 1500);
      
    } catch (err) {
      setEditError("Ошибка при сохранении изменений: " + err.message);
    }
  };

  // Открытие модального окна подтверждения
  const openConfirmModal = (userId, isActive) => {
    setConfirmAction({ userId, isActive });
    setShowConfirmModal(true);
  };

  // Закрытие модального окна подтверждения
  const closeConfirmModal = () => {
    setShowConfirmModal(false);
    setConfirmAction(null);
    setConfirmSuccess(null);
    setConfirmError(null);
  };

  // Выполнение подтвержденного действия
  const executeConfirmedAction = async () => {
    if (!confirmAction) return;
    
    setConfirmError(null);
    setConfirmSuccess(null);
    
    const result = await updateUserActivity(confirmAction.userId, confirmAction.isActive);
    
          if (result) {
      const actionText = confirmAction.isActive ? "активирован" : "деактивирован";
      setConfirmSuccess(`Пользователь успешно ${actionText}`);
      
      // Закрываем модальное окно после задержки
      setTimeout(() => {
        closeConfirmModal();
      }, 1500);
    } else {
      setConfirmError("Произошла ошибка при изменении статуса пользователя. Пожалуйста, попробуйте снова.");
    }
  };

  // Отрисовка списка пользователей
  const renderUsers = () => {
    if (loading) return <Loader message="Загрузка пользователей..." />;
    if (error) return <div className="error">{error}</div>;

    // Выбираем какой список пользователей отображать
    const displayUsers = userListMode === "active" 
      ? activeUsers 
      : userListMode === "inactive" 
        ? inactiveUsers 
        : users;

    return (
      <div className="users-list">
        <h3>Список пользователей</h3>
        
        <div className="user-list-controls">
          <button 
            className={`user-filter-btn ${userListMode === "active" ? "active" : ""}`}
            onClick={() => setUserListMode("active")}
          >
            Активные
          </button>
          <button 
            className={`user-filter-btn ${userListMode === "inactive" ? "active" : ""}`}
            onClick={() => setUserListMode("inactive")}
          >
            Неактивные
          </button>
          <button 
            className={`user-filter-btn ${userListMode === "all" ? "active" : ""}`}
            onClick={() => setUserListMode("all")}
          >
            Все
          </button>
        </div>
        
        <table>
          <thead>
            <tr>
              <th>ID</th>
              <th>Логин</th>
              <th>Email</th>
              <th>Роль</th>
              <th>Статус</th>
              <th>Действия</th>
            </tr>
          </thead>
          <tbody>
            {displayUsers.length > 0 ? (
              displayUsers.map(user => {
                // Явно преобразуем is_active в булево значение
                const isActive = Boolean(user.is_active);
                
                return (
                  <tr key={user.id} className={isActive ? "active-row" : "inactive-row"}>
                    <td>{user.id}</td>
                    <td>{user.login || "-"}</td>
                    <td>{user.email}</td>
                    <td>{user.role_id}</td>
                    <td>
                      <span className={`status-indicator ${isActive ? "status-active" : "status-inactive"}`}>
                        {isActive ? "Активен" : "Неактивен"}
                      </span>
                    </td>
                    <td>
                      <div className="user-action-buttons">
                        <button 
                          className="edit-btn"
                          onClick={() => openEditModal(user)}
                        >
                          Редактировать
                        </button>
                        {isActive ? (
                          <button 
                            className="deactivate-btn"
                            onClick={() => openConfirmModal(user.id, false)}
                          >
                            Деактивировать
                          </button>
                        ) : (
                          <button 
                            className="activate-btn"
                            onClick={() => openConfirmModal(user.id, true)}
                          >
                            Активировать
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })
            ) : (
              <tr>
                <td colSpan="6">
                  {userListMode === "active" 
                    ? "Нет активных пользователей" 
                    : userListMode === "inactive" 
                      ? "Нет неактивных пользователей" 
                      : "Нет пользователей"}
                </td>
              </tr>
            )}
          </tbody>
        </table>
        <button className="add-btn">Добавить пользователя</button>
        
        {/* Модальное окно редактирования пользователя */}
        {showEditModal && editUser && (
          <div className="admin-modal-overlay" onClick={closeEditModal}>
            <div className="admin-modal" onClick={e => e.stopPropagation()}>
              <div className="admin-modal-header">
                <h3>Редактирование</h3>
                <button className="admin-close-btn" onClick={closeEditModal}>×</button>
              </div>
              <div className="admin-modal-body">
                {editError && <div className="edit-error">{editError}</div>}
                {editSuccess && <div className="edit-success">{editSuccess}</div>}
                
                <div className="form-group">
                  <label>Email</label>
                  <input 
                    type="text" 
                    value={editUser.email || ''} 
                    disabled 
                  />
                </div>
                
                <div className="form-group">
                  <label>Логин</label>
                  <input 
                    type="text" 
                    value={editUser.login || ''} 
                    disabled 
                  />
                </div>
                
                <div className="form-group">
                  <label>Роль</label>
                  <select 
                    value={newRole} 
                    onChange={(e) => setNewRole(e.target.value)}
                  >
                    <option value="1">Базовый пользователь</option>
                    <option value="2">Пользователь с подпиской</option>
                    <option value="3">Работник ЖКХ</option>
                    <option value="4">Администратор</option>
                  </select>
                </div>
                
                <div className="form-group">
                  <label>Новый пароль</label>
                  <input 
                    type="password" 
                    value={newPassword} 
                    onChange={(e) => setNewPassword(e.target.value)} 
                    placeholder="Введите новый пароль"
                  />
                </div>
              </div>
              <div className="admin-modal-footer">
                <button className="save-btn" onClick={saveUserChanges}>Сохранить</button>
                <button className="cancel-btn" onClick={closeEditModal}>Отмена</button>
              </div>
            </div>
          </div>
        )}
        
        {/* Модальное окно подтверждения активации/деактивации */}
        {showConfirmModal && confirmAction && (
          <div className="admin-modal-overlay" onClick={closeConfirmModal}>
            <div className="admin-modal" onClick={e => e.stopPropagation()}>
              <div className="admin-modal-header">
                <h3>{confirmAction.isActive ? "Активация пользователя" : "Деактивация пользователя"}</h3>
                <button className="admin-close-btn" onClick={closeConfirmModal}>×</button>
              </div>
              <div className="admin-modal-body">
                {confirmSuccess && <div className="edit-success">{confirmSuccess}</div>}
                {confirmError && <div className="edit-error">{confirmError}</div>}
                
                <p>Вы уверены, что хотите {confirmAction.isActive ? "активировать" : "деактивировать"} пользователя?</p>
                <p>Это действие {confirmAction.isActive ? "разрешит" : "запретит"} пользователю вход в систему.</p>
              </div>
              <div className="admin-modal-footer">
                <button className="save-btn" onClick={executeConfirmedAction}>
                  {confirmAction.isActive ? "Активировать" : "Деактивировать"}
                </button>
                <button className="cancel-btn" onClick={closeConfirmModal}>Отмена</button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };



  // Проверка форматов файлов
  const isValidFileType = (file, types) => {
    if (!file) return false;
    return types.some(type => {
      const extension = file.name.split('.').pop().toLowerCase();
      return extension === type;
    });
  };

  // Обработчик загрузки файла погоды
  const handleWeatherFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (isValidFileType(file, ['xlsx', 'xls'])) {
        setWeatherFile(file);
        setUploadError(null);
      } else {
        setWeatherFile(null);
        setUploadError("Неверный формат файла. Допустимые форматы: .xlsx, .xls");
      }
    }
  };

  // Обработчик загрузки файла датчиков
  const handleSensorFileChange = (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    // Проверяем все выбранные файлы на соответствие формату
    const invalidFiles = files.filter(file => !isValidFileType(file, ['xlsx']));

    if (invalidFiles.length > 0) {
      setUploadError(`Некоторые файлы имеют неверный формат. Допустимый формат: .xlsx`);
      setSensorFiles([]);
      return;
    }

    setSensorFiles(files);
    setUploadError(null);
  };

  // Загрузка файла погоды на сервер
  const uploadWeatherFile = async () => {
    if (!weatherFile) {
      setUploadError("Выберите файл для загрузки");
      return;
    }

    setIsUploading(true);
    setUploadError(null);
    setUploadResult(null);

    try {
      const token = localStorage.getItem("access_token");
      const formData = new FormData();
      formData.append('file', weatherFile);

      const response = await fetch(`${API_URL}/Neuron/upload-weather/`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'accept': 'application/json'
        },
        body: formData,
        credentials: 'include'
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Ошибка при загрузке файла');
      }

      setUploadResult({
        message: data.message,
        filename: data.filename
      });
      setWeatherFile(null);
      document.getElementById('weather-file').value = '';
    } catch (err) {
      setUploadError(err.message || "Произошла ошибка при загрузке файла");
    } finally {
      setIsUploading(false);
    }
  };

  // Загрузка файла датчиков на сервер
  const uploadSensorFile = async () => {
    if (sensorFiles.length === 0) {
      setUploadError("Выберите файлы для загрузки");
      return;
    }

    setIsUploading(true);
    setUploadError(null);
    setUploadResult(null);

    try {
      const token = localStorage.getItem("access_token");
      const formData = new FormData();
      
      // Добавляем все файлы в FormData с тем же именем параметра 'files'
      sensorFiles.forEach(file => {
        formData.append('files', file);
      });

      const response = await fetch(`${API_URL}/Neuron/upload-sensor/`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'accept': 'application/json'
        },
        body: formData,
        credentials: 'include'
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Ошибка при загрузке файлов');
      }

      setUploadResult({
        message: data.message || `Успешно загружено ${sensorFiles.length} файлов`,
        files: data.files || sensorFiles.map(file => file.name),
        count: sensorFiles.length,
        mapping: data.original_to_new_mapping
      });
      setSensorFiles([]);
      document.getElementById('sensor-file').value = '';
    } catch (err) {
      setUploadError(err.message || "Произошла ошибка при загрузке файлов");
    } finally {
      setIsUploading(false);
    }
  };

  // Функция для отправки запроса на прогнозирование
  const runPrediction = async () => {
    setIsUploading(true);
    setUploadError(null);
    
    // Сразу показываем сообщение о постановке в очередь
    setUploadResult({
      message: "Прогноз поставлен в очередь и будет выполнен в фоновом режиме"
    });

    try {
      const token = localStorage.getItem("access_token");
      const response = await fetch(`${API_URL}/Neuron/predict-all/`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'accept': 'application/json'
        },
        credentials: 'include'
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Ошибка при выполнении прогноза');
      }

      setUploadResult({
        message: "Прогноз успешно выполнен",
        details: data
      });
    } catch (err) {
      setUploadError(err.message || "Произошла ошибка при выполнении прогноза");
    } finally {
      setIsUploading(false);
    }
  };

  // Рендер результатов загрузки
  const renderUploadResult = () => {
    if (uploadError) {
      return <div className="upload-error">{uploadError}</div>;
    }

    if (isUploading && !uploadResult) {
      return <Loader message="Загрузка файла..." />;
    }

    if (uploadResult) {
      return (
        <div className="upload-success">
          <h4>Результат загрузки:</h4>
          <p>{uploadResult.message}</p>
          {uploadResult.filename && <p>Имя файла: {uploadResult.filename}</p>}
          {uploadResult.files && (
            <div>
              <p>Загруженные файлы:</p>
              <ul>
                {uploadResult.files.map((file, index) => (
                  <li key={index}>{file}</li>
                ))}
              </ul>
            </div>
          )}
          {uploadResult.mapping && (
            <div>
              <p>Соответствие исходных и новых имен:</p>
              <ul>
                {Object.entries(uploadResult.mapping).map(([original, newName], index) => (
                  <li key={index}>{original} → {newName}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      );
    }

    return null;
  };

  // Рендер секции загрузки данных
  const renderDataUpload = () => {
    return (
      <div className="data-upload">
        <h3>Загрузка данных</h3>
        
        {renderUploadResult()}
        
        <div className="upload-form">
          <div className="upload-section">
            <h4>Загрузка данных о погоде</h4>
            <p className="upload-description">Загрузите файл прогноза погоды в формате .xlsx или .xls</p>
            <div className="upload-input-group">
              <input
                type="file"
                id="weather-file"
                accept=".xlsx,.xls"
                onChange={handleWeatherFileChange}
                className="file-input"
              />
              <button 
                className="upload-btn" 
                onClick={uploadWeatherFile}
                disabled={!weatherFile || isUploading}
              >
                Загрузить
              </button>
            </div>
          </div>
          
          <div className="upload-section">
            <h4>Загрузка данных датчиков</h4>
            <p className="upload-description">Загрузите файлы с данными датчиков в формате .xlsx (можно выбрать несколько)</p>
            <div className="upload-input-group">
              <input
                type="file"
                id="sensor-file"
                accept=".xlsx"
                onChange={handleSensorFileChange}
                multiple
                className="file-input"
              />
              <div className="selected-files">
                {sensorFiles.length > 0 && (
                  <>
                    <p className="selected-files-label">Выбрано файлов: {sensorFiles.length}</p>
                    <ul className="selected-files-list">
                      {sensorFiles.map((file, index) => (
                        <li key={index}>{file.name}</li>
                      ))}
                    </ul>
                  </>
                )}
              </div>
              <button 
                className="upload-btn" 
                onClick={uploadSensorFile}
                disabled={sensorFiles.length === 0 || isUploading}
              >
                Загрузить
              </button>
            </div>
          </div>
        </div>
        
        <div className="predict-section">
          <h4>Выполнение прогноза</h4>
          <p className="predict-description">Запуск прогнозирования на основе загруженных данных</p>
          <div className="predict-button-container">
            <button 
              className="predict-btn" 
              onClick={runPrediction}
              disabled={isUploading}
            >
              ПРОГНОЗ
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="admin-panel">
      <header className="admin-header">
        <h1>Панель администратора</h1>
        <Link to="/" className="back-btn">
          <img src={expandArrow} alt="Назад" className="back-arrow" />
        </Link>
      </header>
      
      <div className="admin-container">
        <div className="admin-sidebar">
          <ul className="admin-nav">
            <li 
              className={activeTab === "users" ? "active" : ""}
              onClick={() => setActiveTab("users")}
            >
              Управление пользователями
            </li>
            <li 
              className={activeTab === "data" ? "active" : ""}
              onClick={() => setActiveTab("data")}
            >
              Загрузка данных
            </li>
          </ul>
        </div>
        
        <div className="admin-content">
          {activeTab === "users" && renderUsers()}
          {activeTab === "data" && renderDataUpload()}
        </div>
      </div>
    </div>
  );
}

export default AdminPanel; 